import React from 'react';
import { Link } from 'react-router-dom';
import { useCountry } from '../../hooks/useCountry';

const TermsOfService = () => {
  const { theme, isSubdomain } = useCountry();
  const colors = theme?.colors || {};

  return (
    <div className={isSubdomain ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header - only on main domain */}
      {!isSubdomain && (
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">RentyDoc</Link>
          </div>
        </header>
      )}

      {/* Hero Banner */}
      <section className="text-white py-8 sm:py-12" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl font-bold">Terms of Service</h1>
          <p className="text-white/70 mt-2">Last updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/50 shadow-sm prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing and using RentyDoc.com ("the Service"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              RentyDoc provides an online platform for generating rental and lease agreement documents. Our Service allows users to create legally-formatted contracts based on questionnaire responses. Documents are provided in PDF format for download after purchase.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Responsibilities</h2>
            <p className="text-gray-700 mb-4">You agree to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide accurate and complete information when using our Service</li>
              <li>Use the Service only for lawful purposes</li>
              <li>Not misrepresent your identity or authority to enter into agreements</li>
              <li>Review all generated documents before use</li>
              <li>Seek independent legal advice if uncertain about any terms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Document Generation and Legal Disclaimer</h2>
            <p className="text-gray-700 mb-4">
              <strong>Important:</strong> RentyDoc provides document templates based on general legal requirements for each jurisdiction. However:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>We are not a law firm and do not provide legal advice</li>
              <li>Documents are templates and may not cover all situations</li>
              <li>Laws vary by jurisdiction and change over time</li>
              <li>You are responsible for ensuring documents meet your specific needs</li>
              <li>We recommend consulting a qualified legal professional for complex situations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Payment and Refunds</h2>
            <p className="text-gray-700 mb-4">
              Payment is required to download final documents. All payments are processed securely through Stripe. Due to the digital nature of our products:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>All sales are final once the document is downloaded</li>
              <li>Refunds may be considered on a case-by-case basis for technical issues</li>
              <li>Contact support within 7 days of purchase for refund requests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              The Service, including its design, features, and content, is owned by RentyDoc. Upon purchase, you receive a license to use the generated document for your personal or business purposes. You may not resell, distribute, or commercially exploit the document templates.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Privacy</h2>
            <p className="text-gray-700 mb-4">
              Your use of the Service is also governed by our <Link to="/privacy" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>Privacy Policy</Link>. By using the Service, you consent to the collection and use of information as described in our Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the maximum extent permitted by law, RentyDoc shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or other intangible losses resulting from your use of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about these Terms, please contact us at:
            </p>
            <p className="text-gray-700">
              Email: <a href="mailto:legal@rentydoc.com" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>legal@rentydoc.com</a><br />
              Or visit our <Link to="/contact" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>Contact Page</Link>
            </p>
          </section>
        </div>
      </div>

      {/* Footer - only on main domain */}
      {!isSubdomain && (
        <footer className="bg-gray-800 text-white py-8 mt-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-gray-400">&copy; 2025 RentyDoc. All rights reserved.</p>
            <div className="mt-4 space-x-4">
              <Link to="/terms" className="text-gray-400 hover:text-white">Terms</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white">Privacy</Link>
              <Link to="/about" className="text-gray-400 hover:text-white">About</Link>
              <Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default TermsOfService;
